const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Button } = require('@mui/material/Button');
const { default: MuiList } = require('@mui/material/List');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CloseIcon } = require('@mui/icons-material/Cancel');
const { default: Tooltip } = require('@mui/material/Tooltip');
const { default: Popover } = require('@mui/material/Popover');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Badge } = require('@mui/material/Badge');
const { default: FilterListIcon } = require('@mui/icons-material/FilterList');
const FullScreenDialog = require('containers/FullScreenDialog');
const NotificationItem = require('./NotificationItem');
const SimpleDialog = require('components/SimpleDialog');
const { analyticsTemplates } = require('utils/analytics');

const {
    useState,
    useEffect,
    useCallback,
    useMemo
} = React;

const NOTIFICATION_TYPE_LABELS = {
    'peer': 'Connections',
    'transfer': 'Transfer Invites',
    'announcement': 'Announcements',
    'question': 'Questions',
    'group-invite': 'Group Invites',
    'survey': 'Surveys',
    'reaction': 'Reactions',
    'offer': 'Offers',
    'resource': 'Resources',
    'mention': 'Mentions',
    'school-invite': 'School Invites',
    'welcome': 'Welcome'
};

const internals = {};

module.exports = function NotificationsDialog(props) {

    const {
        onRequestClose,
        notifications,
        onAcceptTransfer,
        onClickAnswerQuestion,
        onDismissNotification,
        dismissAllNotifications,
        currentUserRolesInteractions,
        showNotification,
        open,
        // eslint-disable-next-line no-unused-vars
        onClickJoinClass,
        // eslint-disable-next-line no-unused-vars
        onRequestIgnore,
        // eslint-disable-next-line no-unused-vars
        onRequestAccept,
        // eslint-disable-next-line no-unused-vars
        markAllNotifsAsRead,
        ...rest
    } = props;

    const {
        Title,
        List,
        NotificationCard,
        NotificationItemContainer,
        NotificationDismissRail,
        CloseIconButton,
        DismissButtonContainer,
        NoNotifsContainer,
        FilterPopoverContent,
        FilterHeader,
        FilterTypeItem
    } = internals;

    const [alertInfo, setAlertInfo] = useState({
        alertTitle: '',
        alertMessage: ''
    });
    const [alertOpen, setAlertOpen] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const availableTypes = useMemo(() => {

        return [...new Set(notifications.map((n) => n.type))];
    }, [notifications]);

    const [selectedTypes, setSelectedTypes] = useState(() => new Set(availableTypes));

    useEffect(() => {

        setSelectedTypes((prev) => {

            const next = new Set(prev);
            availableTypes.forEach((t) => next.add(t));
            return next;
        });
    }, [availableTypes.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleType = useCallback((type) => {

        setSelectedTypes((prev) => {

            const next = new Set(prev);
            if (next.has(type)) {
                next.delete(type);
            }
            else {
                next.add(type);
            }

            return next;
        });
    }, []);

    const isFiltered = availableTypes.some((t) => !selectedTypes.has(t));

    const filteredNotifications = notifications.filter((n) => selectedTypes.has(n.type));

    const filterOpen = Boolean(filterAnchorEl);

    // Mark notifs as read when opening the dialog
    useEffect(() => {

        if (open) {
            markAllNotifsAsRead();
        }
    }, [open]);

    const accept = useCallback((notification) => {

        onRequestAccept(notification);
    }, [onRequestAccept]);

    const ignore = useCallback((notification) => {

        setAlertInfo({
            alertMessage: `Are you sure you want to ignore the request from ${notification.sender.firstName}?`,
            alertTitle: 'Ignore User request',
            confirmFunc: () => {

                analyticsTemplates.buttons('confirm ignore request from user', 'profile: confirm ignore');
                onRequestIgnore(notification);
            },
            cancelFunc: () => {

                analyticsTemplates.buttons('cancel ignore request from user', 'profile: cancel ignore');
            }
        });

        setAlertOpen(true);
    }, [onRequestIgnore]);

    return (
        <FullScreenDialog
            title={<Title>Notifications</Title>}
            onRequestClose={onRequestClose}
            open={open}
            {...rest}
        >
            <SimpleDialog
                isOpen={alertOpen}
                onRequestClose={() => setAlertOpen(false)}
                title={alertInfo.alertTitle}
                content={alertInfo.alertMessage}
                buttons={<React.Fragment>
                    <Button
                        onClick={() => {
                            setAlertOpen(false);
                            alertInfo.cancelFunc?.();
                        }}
                        color='secondary'
                        variant='contained'
                        data-focus-outline='radius:40'
                    >
                        No
                    </Button>
                    <Button
                        onClick={() => {
                            setAlertOpen(false);
                            alertInfo.confirmFunc?.();
                        }}
                        color='primary'
                        variant='contained'
                        data-focus-outline='radius:40'
                    >
                        Ok
                    </Button>
                </React.Fragment>}
            />
            {!!notifications.length && (
                <DismissButtonContainer>
                    <Button
                        size='small'
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                        aria-label='Filter notifications'
                        data-focus-outline='radius:20'
                        startIcon={
                            <Badge color='primary' variant='dot' invisible={!isFiltered}>
                                <FilterListIcon fontSize='small' />
                            </Badge>
                        }
                        sx={{
                            textTransform: 'none',
                            fontSize: 14,
                            fontWeight: 500,
                            color: isFiltered ? 'primary.main' : 'text.secondary'
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        data-focus-outline='radius:20'
                        color='secondary'
                        variant='contained'
                        size='small'
                        disableElevation
                        sx={{
                            minWidth: 0,
                            minHeight: 30,
                            borderRadius: 999,
                            px: 2,
                            py: .25,
                            fontSize: 14,
                            lineHeight: 1.2,
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { boxShadow: 'none' },
                            '&.Mui-focusVisible': { boxShadow: 'none' }
                        }}
                        onClick={() => dismissAllNotifications(filteredNotifications.map((n) => n.id))}
                    >
                        Dismiss all
                    </Button>
                </DismissButtonContainer>
            )}
            <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <FilterPopoverContent>
                    <FilterHeader>Filter by type</FilterHeader>
                    <FilterTypeItem>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size='small'
                                    checked={availableTypes.every((t) => selectedTypes.has(t))}
                                    indeterminate={isFiltered && availableTypes.some((t) => selectedTypes.has(t))}
                                    onChange={() => {

                                        if (availableTypes.every((t) => selectedTypes.has(t))) {
                                            setSelectedTypes(new Set());
                                        }
                                        else {
                                            setSelectedTypes(new Set(availableTypes));
                                        }
                                    }}
                                />
                            }
                            label={<b>All</b>}
                        />
                    </FilterTypeItem>
                    {availableTypes.map((type) => (
                        <FilterTypeItem key={type}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size='small'
                                        checked={selectedTypes.has(type)}
                                        onChange={() => toggleType(type)}
                                    />
                                }
                                label={NOTIFICATION_TYPE_LABELS[type] || type}
                            />
                        </FilterTypeItem>
                    ))}
                </FilterPopoverContent>
            </Popover>
            <List
                tabIndex={0}
                aria-label={filteredNotifications.length ? 'Notification items' : 'No new notifications'}
                data-focus-outline='radius:5'
            >
                {!filteredNotifications.length && (
                    <NoNotifsContainer>
                        {notifications.length ? 'No notifications match the selected filters.' : 'There are no new notifications.'}
                    </NoNotifsContainer>
                )}
                {filteredNotifications.map((notification) => {

                    return (
                        <NotificationCard key={notification.id}>
                            <NotificationItemContainer>
                                <NotificationItem
                                    notification={notification}
                                    onLeave={onRequestClose}
                                    onAcceptTransfer={onAcceptTransfer}
                                    onRequestIgnore={ignore}
                                    onRequestAccept={accept}
                                    onClickJoinClass={onClickJoinClass}
                                    onClickAnswerQuestion={onClickAnswerQuestion}
                                    onDismissNotification={onDismissNotification}
                                    currentUserRolesInteractions={currentUserRolesInteractions}
                                    showNotification={showNotification}
                                />
                                <NotificationDismissRail>

                                    <CloseIconButton
                                        onClick={() => onDismissNotification(notification)}
                                        aria-label='Dismiss notification'
                                        data-focus-outline='radius:30'
                                    >
                                        <Tooltip
                                            arrow
                                            title='Dismiss'
                                            placement='top'
                                        >
                                            <CloseIcon style={{ fontSize: 18 }} />
                                        </Tooltip>
                                    </CloseIconButton>

                                </NotificationDismissRail>
                            </NotificationItemContainer>

                        </NotificationCard>
                    );
                })}
            </List>
        </FullScreenDialog>
    );
};

module.exports.propTypes = {
    onRequestClose: T.func.isRequired,
    onAcceptTransfer: T.func.isRequired,
    onRequestIgnore: T.func.isRequired,
    onRequestAccept: T.func.isRequired,
    markAllNotifsAsRead: T.func.isRequired,
    open: T.bool,
    notifications: T.arrayOf(T.shape({
        id: T.any.isRequired
    })),
    onClickJoinClass: T.func,
    showNotification: T.func,
    onClickAnswerQuestion: T.func,
    dismissAllNotifications: T.func,
    onDismissNotification: T.func,
    currentUserRolesInteractions:T.arrayOf(T.shape({
        id:T.number,
        name:T.string,
        label:T.string,
        schoolId:T.number,
        canViewProfile:T.bool
    }))
};

internals.Title = Styled.span`
    width: 100%;
    display: inline-block;
    text-align: center;
`;

internals.List = Styled(MuiList)`
    width: 100%;
    margin: 0 auto;
    padding: 0 0 50px;
    box-sizing: border-box;
`;

internals.NotificationCard = Styled.div`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: stretch;
    margin: 4px 0 14px;
    padding: 8px 0 8px 12px;
    border-radius: 16px;
    background: rgba(248, 250, 252, 0.96);
    border: 1px solid rgba(18, 39, 79, 0.09);
    overflow: hidden;
    transition: background 120ms ease, border-color 120ms ease;

    &:hover {
        background: ${({ theme }) => theme.palette.primary.main}0F;
        border-color: ${({ theme }) => theme.palette.primary.main}33;
    }
`;



internals.NotificationItemContainer = Styled.div`
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: stretch;

    li {
        flex: 1;
        min-width: 0;
        background: transparent !important;
        padding-top: 6px;
        padding-bottom: 6px;
    }

    li > div {
        min-height: 50px;
    }
`;




internals.NotificationDismissRail = Styled.div`
    width: 40px;
    flex: 0 0 40px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    align-self: stretch;
    padding-top: 10px;
    margin-left: 4px;
    background: transparent;
    pointer-events: none;
`;


internals.CloseIconButton = Styled(IconButton)`
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    padding: 2px !important;
    margin: 0 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    pointer-events: auto;

    &:hover,
    &.Mui-focusVisible {
        box-shadow: none !important;
        background: ${({ theme }) => theme.palette.primary.main}14 !important;
    }
`;




internals.DismissButtonContainer = Styled.div`
    width: 100%;
    margin: 0 auto;
    padding: 0 0 16px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

internals.FilterPopoverContent = Styled.div`
    padding: 8px 4px;
    min-width: 210px;
`;

internals.FilterHeader = Styled.div`
    padding: 4px 12px 8px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

internals.FilterTypeItem = Styled.div`
    padding: 0 8px;

    .MuiFormControlLabel-root {
        width: 100%;
        margin: 0;
        padding: 2px 0;
    }

    .MuiFormControlLabel-label {
        font-size: 14px;
    }
`;




internals.NoNotifsContainer = Styled.div`
    width: 100%;
    text-align: center;
    padding: 40px 20px;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87);
`;
